import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { api, type Me, type Site } from '../api/client';

interface SessionValue {
  me: Me;
  sites: Site[];
  site: Site | null;
  setSiteId: (id: string) => void;
  logout: () => Promise<void>;
  can: (role: Me['role']) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);
const RANK: Record<Me['role'], number> = { VIEWER: 0, OPERATOR: 1, ADMIN: 2, OWNER: 3 };
const SITE_KEY = 'pulseiptv.siteId';

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.get<{ items: Site[] }>('/sites') });
  const [siteId, setSiteIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SITE_KEY);
    } catch {
      return null;
    }
  });
  const items = sites.data?.items ?? [];
  const site = items.find((s) => s.id === siteId) ?? items[0] ?? null;

  useEffect(() => {
    if (site && site.id !== siteId) setSiteIdState(site.id);
  }, [site, siteId]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      sites: items,
      site,
      setSiteId: (id) => {
        setSiteIdState(id);
        try {
          localStorage.setItem(SITE_KEY, id);
        } catch {
          /* private mode */
        }
      },
      logout: async () => {
        await api.post('/auth/logout');
        qc.clear();
        window.location.href = '/login';
      },
      can: (role) => RANK[me.role] >= RANK[role],
    }),
    [me, items, site, qc],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** The selected site; pages that need one render a hint when none exists yet. */
export function useSite(): Site | null {
  return useSession().site;
}
